import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Calendar } from "lucide-react";
import { SERVICES } from "@/constants/content";

const URGENCY = [
  { value: "standard", label: "Standard (within 24h)" },
  { value: "urgent", label: "Urgent (within few hours)" },
  { value: "emergency", label: "Emergency (ASAP)" },
];

const initial = {
  full_name: "", phone: "", email: "", service_type: "Home Nursing",
  patient_age: "", city: "", address: "", medical_condition: "",
  preferred_datetime: "", urgency: "standard", message: "",
};

export default function BookingDialog({ open, onOpenChange, presetService }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (presetService) setForm((p) => ({ ...p, service_type: presetService }));
  }, [presetService]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.city || !form.service_type) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.patient_age) delete payload.patient_age;
      if (!payload.message) delete payload.message;
      await api.post("/bookings", payload);
      toast.success("Appointment booked! Our care coordinator will call you shortly.");
      setForm(initial);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#0A84FF] to-[#10B981] text-white flex items-center justify-center">
              <Calendar className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="font-display text-xl">Book an Appointment</DialogTitle>
              <DialogDescription>Share patient details — we'll call you within minutes.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Full Name *</Label>
              <Input value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="Patient / contact name" className="mt-1.5" required data-testid="booking-name" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Phone *</Label>
              <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91 XXXXX XXXXX" className="mt-1.5" required data-testid="booking-phone" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" className="mt-1.5" data-testid="booking-email" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Patient Age</Label>
              <Input value={form.patient_age} onChange={(e) => update("patient_age", e.target.value)} placeholder="e.g. 68" className="mt-1.5" data-testid="booking-age" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Service Required *</Label>
              <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
                <SelectTrigger className="mt-1.5" data-testid="booking-service">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICES.map((s) => (
                    <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>
                  ))}
                  <SelectItem value="Other">Other / Not Sure</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Urgency</Label>
              <Select value={form.urgency} onValueChange={(v) => update("urgency", v)}>
                <SelectTrigger className="mt-1.5" data-testid="booking-urgency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {URGENCY.map((u) => (<SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold">City / Area *</Label>
            <Input value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="Noida, Gurugram, Delhi..." className="mt-1.5" required data-testid="booking-city" />
          </div>

          <div>
            <Label className="text-xs font-semibold">Address</Label>
            <Input value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="Flat/House, Street, Locality" className="mt-1.5" data-testid="booking-address" />
          </div>

          <div>
            <Label className="text-xs font-semibold">Preferred Date &amp; Time</Label>
            <Input type="datetime-local" value={form.preferred_datetime} onChange={(e) => update("preferred_datetime", e.target.value)} className="mt-1.5" data-testid="booking-datetime" />
          </div>

          <div>
            <Label className="text-xs font-semibold">Medical Condition</Label>
            <Textarea value={form.medical_condition} onChange={(e) => update("medical_condition", e.target.value)} rows={2} placeholder="Any diagnosed conditions, current medications, allergies..." className="mt-1.5" data-testid="booking-condition" />
          </div>

          <div>
            <Label className="text-xs font-semibold">Additional Notes</Label>
            <Textarea value={form.message} onChange={(e) => update("message", e.target.value)} rows={2} placeholder="Anything else our care team should know..." className="mt-1.5" data-testid="booking-message" />
          </div>

          <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#0A84FF] hover:bg-[#0070d9] text-white h-11 font-semibold" data-testid="booking-submit-btn">
            {loading ? "Submitting..." : "Confirm Appointment"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
